import { CANVAS_ELEMENT_DRAG } from '../../PreviewDisplayConstants';
import { toBaseUnitBox } from '../canvasElementSnapping';

// What kind of element a target stands for. Only the highlight and the collectors care: the drag
// controller itself never asks, which is the point of the adapter.
export const DRAG_TARGET_TYPE = {
    IMAGE: 'image',
    TEXT: 'text',
    VISUALIZER: 'visualizer',
    LYRICS: 'lyrics'
};

// One draggable thing on the preview canvas, whatever it is drawn from. The controller moves a target
// while the pointer is down and writes its position to the store once on release; everything it needs
// to know about a picture, a line of text or the visualizer is behind these few methods.
//
// A subclass supplies the three things only it can know: where the element is drawn (getBounds), where
// it is stored (getPosition), and how to put it somewhere else now (moveTo) and for good (commit).
export default class CanvasDragTarget {

    constructor(id, type) {
        // Identity survives the drag: the target objects themselves are rebuilt on every pointer event
        // from whatever the pipeline is holding, so nothing may be compared by reference.
        this.id = id;
        this.type = type;
    }

    // Corner resize squares are an image element's alone. Text and the visualizer are dragged only.
    get canResize() {
        return false;
    }

    // The element's rectangle on the renderer's pixel grid, or null while it has nothing to show.
    getBounds() {
        throw new Error('getBounds must be implemented by the target');
    }

    // The stored position, in base units measured from the middle of the frame.
    getPosition() {
        throw new Error('getPosition must be implemented by the target');
    }

    // Moves the element on the display tree alone. Called once per animation frame for the length of a
    // drag, so it must not go anywhere near the store.
    moveTo() {
        throw new Error('moveTo must be implemented by the target');
    }

    // Writes the position to the store. Called once, on release, so a whole drag is one undo step.
    commit() {
        throw new Error('commit must be implemented by the target');
    }

    // Told when the pointer takes hold of the element and when it lets go. Nothing to do for most
    // elements, which stay as they are for as long as they are held; the lyrics are the exception,
    // because the line under the pointer would otherwise come and go with the playback time.
    startDrag() {}

    stopDrag() {}

    // The element's rectangle in base units, which is the space snapping and the position limits work
    // in. Null when the element has nothing on screen to align by or to grab.
    measureBox() {
        return toBaseUnitBox(this.getBounds());
    }

    // How far the element's box sits from its stored position, in base units, for the position limits
    // to hold the box on the frame rather than just the position. None here: a picture is drawn around
    // its position, and the visualizer is held by its position alone, so a drag stops exactly where
    // its X and Y Position sliders do.
    measureOffsetFromPosition() {
        return { x: 0, y: 0 };
    }

    // The lines this element offers for another one being dragged to catch on: its two edges and its
    // middle, per axis. Edges are worth offering because two elements butted up against each other is
    // as much an alignment as two sharing a middle.
    measureSnapLines() {
        const box = this.measureBox();

        if (!box)
            return null;

        return {
            x: [box.centerX - box.halfWidth, box.centerX, box.centerX + box.halfWidth],
            y: [box.centerY - box.halfHeight, box.centerY, box.centerY + box.halfHeight]
        };
    }

    // Whether a pointer, in base units, is on the element. The box is grown to a minimum first: a flat
    // wave with no base height and a single line of small text are both only a few units thick, and
    // neither could be caught at its true size.
    containsPoint(point) {
        const box = this.measureBox();

        if (!box)
            return false;

        const minimumHalfSize = CANVAS_ELEMENT_DRAG.MINIMUM_GRAB_SIZE * 0.5;

        return Math.abs(point.x - box.centerX) <= Math.max(box.halfWidth, minimumHalfSize)
            && Math.abs(point.y - box.centerY) <= Math.max(box.halfHeight, minimumHalfSize);
    }
}

// The rectangle covering every one of the given display objects that is actually being drawn. PIXI
// reads a container's children rather than the container itself, so an element hidden by its own
// visible flag still reports the bounds it had when it was last shown; each one is checked here.
export function measureVisibleBounds(displayObjects) {
    return displayObjects
        .filter(displayObject => displayObject && displayObject.visible && displayObject.renderable)
        .map(displayObject => displayObject.getBounds())
        .filter(bounds => bounds.width > 0 && bounds.height > 0)
        .reduce(uniteBounds, null);
}

// A rectangle in a display object's own space, as the rectangle it covers on the renderer's pixel grid.
// Every corner is carried through the object's whole transform, so the result stays right while an
// effect such as drift scales or turns the element. Null for a rectangle with no area.
export function measureTransformedBounds(displayObject, localRectangle) {
    if (!(localRectangle.width > 0 && localRectangle.height > 0))
        return null;

    const left = localRectangle.x;
    const top = localRectangle.y;
    const right = left + localRectangle.width;
    const bottom = top + localRectangle.height;

    // Only the first corner brings the world transform up to date; the other three reuse it.
    const corners = [{ x: left, y: top }, { x: right, y: top }, { x: right, y: bottom }, { x: left, y: bottom }]
        .map((corner, index) => displayObject.toGlobal(corner, undefined, index > 0));

    const horizontalCoordinates = corners.map(corner => corner.x);
    const verticalCoordinates = corners.map(corner => corner.y);
    const lowestX = Math.min(...horizontalCoordinates);
    const lowestY = Math.min(...verticalCoordinates);

    return {
        x: lowestX,
        y: lowestY,
        width: Math.max(...horizontalCoordinates) - lowestX,
        height: Math.max(...verticalCoordinates) - lowestY
    };
}

function uniteBounds(united, bounds) {
    if (!united)
        return bounds.clone();

    const left = Math.min(united.x, bounds.x);
    const top = Math.min(united.y, bounds.y);

    united.width = Math.max(united.x + united.width, bounds.x + bounds.width) - left;
    united.height = Math.max(united.y + united.height, bounds.y + bounds.height) - top;
    united.x = left;
    united.y = top;

    return united;
}
